'use client';

import { useAppTranslation, useLocale } from '@freedraw/i18n/react';
import type { AppLocale } from '@freedraw/i18n/shared';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useId, useState } from 'react';

import { BRAND } from '@/theme';

// Inline SVG flags: emoji flags do not render on Windows.
function UkFlag() {
  const id = useId();
  return (
    <svg viewBox="0 0 60 30" width="100%" height="100%" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
      <clipPath id={`${id}-s`}>
        <path d="M0,0 v30 h60 v-30 z" />
      </clipPath>
      <clipPath id={`${id}-t`}>
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <g clipPath={`url(#${id}-s)`}>
        <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath={`url(#${id}-t)`} stroke="#C8102E" strokeWidth="4" />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}

function VietnamFlag() {
  return (
    <svg viewBox="0 0 30 20" width="100%" height="100%" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
      <rect width="30" height="20" fill="#DA251D" />
      <polygon
        fill="#FFFF00"
        points="15.00,4.00 16.41,8.06 20.71,8.15 17.28,10.74 18.53,14.85 15.00,12.40 11.47,14.85 12.72,10.74 9.29,8.15 13.59,8.06"
      />
    </svg>
  );
}

// Names are shown in their own language so every visitor can find theirs.
const LANGUAGES: ReadonlyArray<{ locale: AppLocale; name: string; Flag: () => React.JSX.Element }> = [
  { locale: 'en', name: 'English', Flag: UkFlag },
  { locale: 'vi', name: 'Tiếng Việt', Flag: VietnamFlag }
];

function FlagBadge({ Flag, plain = false }: { Flag: () => React.JSX.Element; plain?: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        width: 22,
        height: 15,
        borderRadius: '3px',
        overflow: 'hidden',
        display: 'inline-flex',
        flexShrink: 0,
        boxShadow: plain ? 'none' : '0 0 0 1px rgba(17, 17, 17, 0.12)'
      }}
    >
      <Flag />
    </Box>
  );
}

type LanguageSwitcherProps = {
  // "row": label + one flag button per language (board menu).
  // "compact": flag-only dropdown (landing, login and legal headers).
  variant?: 'row' | 'compact' | 'flag';
};

export function LanguageSwitcher({ variant = 'row' }: LanguageSwitcherProps) {
  const { locale, setLocale } = useLocale();
  const { t } = useAppTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (variant === 'flag') {
    const current = LANGUAGES.find((language) => language.locale === locale) ?? LANGUAGES[0]!;
    return (
      <>
        <Tooltip title={t('language.label')} arrow>
          <IconButton
            data-testid="language-switcher"
            aria-label={`${t('language.label')}: ${current.name}`}
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorEl)}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{ width: 36, height: 36, p: 0.7, border: 'none', borderRadius: '50%',
              bgcolor: 'transparent', boxShadow: 'none',
              '&:hover': { bgcolor: 'rgba(230,30,115,0.07)' },
              '&.Mui-focusVisible': { outline: `2px solid ${BRAND.pinkStrong}`, outlineOffset: 2 } }}
          >
            <FlagBadge Flag={current.Flag} plain />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 0.5, py: 0.5, minWidth: 96, borderRadius: '12px', boxShadow: '0 14px 34px rgba(23,26,34,0.13)' } } }}>
          {LANGUAGES.map(({ locale: itemLocale, name, Flag }) => (
            <MenuItem key={itemLocale} data-testid={`language-option-${itemLocale}`}
              aria-label={name} title={name} selected={locale === itemLocale}
              onClick={() => { setLocale(itemLocale); setAnchorEl(null); }}
              sx={{ justifyContent: 'center', minHeight: 38, bgcolor: 'transparent',
                '&.Mui-selected': { bgcolor: 'transparent' },
                '&:hover, &.Mui-selected:hover': { bgcolor: 'rgba(230,30,115,0.07)' } }}>
              <FlagBadge Flag={Flag} plain />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  if (variant === 'compact') {
    return (
      <Select
        size="small"
        value={locale}
        onChange={(event) => setLocale(event.target.value as AppLocale)}
        inputProps={{ 'aria-label': t('language.label') }}
        data-testid="language-switcher"
        renderValue={(value) => {
          const current = LANGUAGES.find((item) => item.locale === value) ?? LANGUAGES[0]!;
          return <FlagBadge Flag={current.Flag} />;
        }}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          transformOrigin: { vertical: 'top', horizontal: 'right' },
          slotProps: { paper: { sx: { minWidth: 0 } } }
        }}
        sx={{
          borderRadius: '8px',
          bgcolor: 'rgba(255, 255, 255, 0.7)',
          '& .MuiSelect-select': { py: 0.75, pl: 1.1, display: 'flex', alignItems: 'center' }
        }}
      >
        {LANGUAGES.map(({ locale: itemLocale, name, Flag }) => (
          <MenuItem
            key={itemLocale}
            value={itemLocale}
            aria-label={name}
            title={name}
            data-testid={`language-option-${itemLocale}`}
            sx={{ justifyContent: 'center', px: 1.5 }}
          >
            <FlagBadge Flag={Flag} />
          </MenuItem>
        ))}
      </Select>
    );
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600 }}>{t('language.label')}</Typography>
      <Stack direction="row" spacing={0.5} data-testid="language-switcher">
        {LANGUAGES.map(({ locale: itemLocale, name, Flag }) => {
          const isActive = locale === itemLocale;
          return (
            <Tooltip key={itemLocale} title={name} arrow>
              <IconButton
                size="small"
                aria-label={name}
                aria-pressed={isActive}
                data-testid={`language-option-${itemLocale}`}
                onClick={() => setLocale(itemLocale)}
                sx={{
                  width: 34,
                  height: 28,
                  borderRadius: '7px',
                  border: `1.5px solid ${isActive ? BRAND.pinkStrong : 'transparent'}`,
                  bgcolor: isActive ? BRAND.pinkLight : 'transparent',
                  opacity: isActive ? 1 : 0.6,
                  '&:hover': { bgcolor: BRAND.pinkLight, opacity: 1 }
                }}
              >
                <FlagBadge Flag={Flag} />
              </IconButton>
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );
}
